import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import config from 'garaje/config/environment';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import { modifyDateInTimeZone } from 'garaje/utils/date-fns-tz-utilities';
import {
  DATE_RANGE_PRESETS,
  formatForDateTimeQueryParam,
  parseStartDate,
  parseEndDate,
  todayAsString,
} from 'garaje/utils/date-range-helper';

// ── Step-group types ───────────────────────────────────────────────────────
export interface AttemptEntry {
  attemptNumber: number | null;
  status: string;
  timestamp: string;
}

export interface CascadeSubstep {
  label: string;
  status: string;
  timestamp?: string;
  isActive?: boolean;
  entryId?: number;
  attempts?: AttemptEntry[];
}

export interface CascadeStepLeaf {
  type: 'leaf';
  id: number;
  label: string;
  status: string;
  timestamp: string;
  isActive: boolean;
}

export interface CascadeStepGroupItem {
  type: 'group';
  id: string;
  label: string;
  status: string;
  summary: string;
  isActive: boolean;
  defaultOpen: boolean;
  substeps: CascadeSubstep[];
}

export type CascadeStepItem = CascadeStepLeaf | CascadeStepGroupItem;

function formatStepDate(iso: string, tz: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    timeZone: tz,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatStepTime(iso: string, tz: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const timeStr = d.toLocaleTimeString('en-US', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  const abbr =
    new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(d)
      .find((p) => p.type === 'timeZoneName')?.value ?? '';
  return abbr ? `${timeStr} ${abbr}` : timeStr;
}

// Legacy-alias fields for the table + cascade drawer templates. Drawer was
// authored against the pre-EIP-3394 entry shape; rather than rewriting a
// 340-line template, project the AuditEventView payload with the old field
// names alongside the raw ones.
//
// Label + routine correlation are backend-authoritative:
//   • `step_display_name` is denormalized onto every row (hris-lifecycle
//     PR #60). Fall back to `audit_event_kind` only for legacy rows.
//   • `routine_run_id` was removed from the wire; correlate audit rows
//     within a routine via `correlation_id`.
function enrichEntry(raw: AuditEntry, tz: string): DisplayAuditEntry {
  const isCompanyScope = raw.scope === 'COMPANY';
  return {
    ...raw,
    timestamp: formatStepTime(raw.created_at, tz),
    timestamp_date: formatStepDate(raw.created_at, tz),
    status: resolveStatusLabel(raw),
    cascade_status: resolveStatusLabel(raw),
    activity: resolveActivityLabel(raw),
    employee_name: raw.employee_full_name ?? '',
    integration: integrationLabel(raw.integration_type),
    location: isCompanyScope ? 'Global' : raw.location_name,
    initiated_by: raw.initiated_by_label ?? 'SYSTEM',
    event_id: raw.audit_event_uuid,
    execution_id: raw.inbound_event_id,
    routine_id: raw.correlation_id,
    fail_reason: raw.failure_reason,
    vendor_error: raw.vendor_response_payload,
  };
}

export interface DisplayAuditEntry extends AuditEntry {
  timestamp: string;
  timestamp_date: string;
  status: string;
  cascade_status: string;
  activity: string;
  employee_name: string;
  integration: string | undefined;
  location: string | undefined;
  initiated_by: string;
  event_id: string;
  execution_id: string | undefined;
  routine_id: string | undefined;
  fail_reason: string | undefined;
  vendor_error: string | undefined;
}

// New API field names from hris-lifecycle AuditEventView
export interface AuditEntry {
  id: number;
  audit_event_uuid: string;
  inbound_event_id?: string;
  step_id?: number;
  correlation_id?: string;
  company_id: number;
  audit_event_kind: string;
  step_type?: string;
  step_display_name?: string;
  scope?: string;
  integration_type?: string;
  location_id?: number;
  location_name?: string;
  mode?: string;
  attempt_number?: number;
  initiated_by_user_id?: number;
  initiated_by_label?: string;
  result?: string;
  display_status?: string;
  failure_reason?: string;
  vendor_response_payload?: string;
  acs_cardholder_external_id?: string;
  blocklist_entry_id?: string;
  reason?: string;
  before_state?: string;
  after_state?: string;
  employee_full_name?: string;
  employee_email?: string;
  created_at: string;
}

interface ColumnToggleKeys {
  colIntegration: boolean;
  colLocation: boolean;
  colMode: boolean;
  colAttempt: boolean;
  colInitiatedBy: boolean;
  colEventId: boolean;
}

// Step-type display names mirror AuditEventDisplayLabel.STEP_TYPE_LABELS on the
// backend. These filter by step_type (not audit_event_kind), so selecting one
// shows all STEP_COMPLETED + STEP_DISMISSED rows for that step type only.
// Routine-level options filter by audit_event_kind as before.
const STEP_TYPE_OPTION_PREFIX = 'step_type:';

const ACTIVITY_OPTIONS = [
  // Routine-level events — filter by audit_event_kind
  { value: 'ROUTINE_ENQUEUED', label: 'Routine enqueued' },
  { value: 'ROUTINE_STARTED', label: 'Routine started' },
  { value: 'ROUTINE_EXPEDITED', label: 'Routine expedited' },
  { value: 'ROUTINE_COMPLETED', label: 'Routine completed' },
  { value: 'ROUTINE_CANCELLED', label: 'Routine cancelled' },
  { value: 'ROUTINE_FAILED', label: 'Routine failed' },
  { value: 'ROUTINE_REVERSE_COMPLETED', label: 'Routine reversed' },
  { value: 'ROUTINE_BULK_PAUSED', label: 'Routine paused' },
  { value: 'ROUTINE_BULK_APPROVED', label: 'Routine approved' },
  { value: 'ROUTINE_RECEIVED_NO_CONFIG', label: 'Routine received (no config)' },
  { value: 'ROUTINE_RECEIVED_DEFERRED', label: 'Routine deferred' },
  { value: 'NOTIFICATION_SENT', label: 'Notification sent' },
  // Step-type display names — filter by step_type (value is prefixed with "step_type:")
  { value: `${STEP_TYPE_OPTION_PREFIX}LOCATION_ACS_DEPROVISION`, label: 'Deprovisioned ACS credentials' },
  { value: `${STEP_TYPE_OPTION_PREFIX}COMPANY_BLOCKLIST_ADD`, label: 'Added to visitor blocklist' },
  { value: `${STEP_TYPE_OPTION_PREFIX}COMPANY_FORCE_SIGNOUT`, label: 'Forced sign-out' },
];

const STATUS_OPTIONS = [
  { value: 'SUCCESS', label: 'Success' },
  { value: 'FAILURE', label: 'Failure' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'SKIPPED', label: 'Skipped' },
  { value: 'PARTIAL', label: 'Partial' },
];

// Prefers the backend-computed display_status (one of: Success, Failure,
// Cancelled, Skipped, Partial). Falls back to raw result for old rows that
// predate the displayStatus field, or '' when no status applies (routine
// lifecycle events like ENQUEUED / STARTED).
function resolveStatusLabel(raw: AuditEntry): string {
  if (raw.display_status) return raw.display_status;
  return '';
}

// Returns a human-readable activity label. Prefers backend step_display_name,
// then step_type lookup in ACTIVITY_OPTIONS, then audit_event_kind lookup.
function resolveActivityLabel(raw: AuditEntry): string {
  if (raw.step_display_name) return raw.step_display_name;
  if (raw.step_type) {
    const stepOpt = ACTIVITY_OPTIONS.find((o) => o.value === `${STEP_TYPE_OPTION_PREFIX}${raw.step_type}`);
    if (stepOpt) return stepOpt.label;
  }
  const kindOpt = ACTIVITY_OPTIONS.find((o) => o.value === raw.audit_event_kind);
  return kindOpt?.label ?? raw.audit_event_kind;
}

// M1 ACS vendors supported by hris-lifecycle. Backend `findDistinctIntegrations`
// derives the dropdown from actual event rows, so vendors that haven't yet had
// a termination routine run against them won't appear. Union client-side so
// admins can filter on the full supported set before events accumulate.
// Long-term fix belongs on the backend (return the supported vendor set, not
// just observed values); remove this constant once that lands.
const KNOWN_INTEGRATIONS = ['BRIVO', 'CCURE', 'GENEA', 'GENETEC', 'KANTECH', 'S2'];

// Sentinel option value for the "Global" (company-scope) entry in the
// Locations dropdown. Selecting it (alone or alongside specific locations)
// sets `include_global=true` on the audit-events query so the backend also
// returns rows where `location_id` is NULL — routine-level events plus any
// COMPANY-scope step events.
const GLOBAL_LOCATION_VALUE = '__global__';

// Sentence-case display labels for ACS vendors. Backend stores + emits
// `integration_type` as uppercase (`KANTECH`); the audit log table + drawer
// render the branded form to match the Integration Health Dashboard.
const INTEGRATION_LABELS: Record<string, string> = {
  BRIVO: 'Brivo',
  CCURE: 'C-Cure',
  GENEA: 'Genea',
  GENETEC: 'Genetec',
  KANTECH: 'Kantech',
  NETBOX: 'NetBox',
  S2: 'S2',
};

function integrationLabel(raw: string | undefined): string | undefined {
  if (!raw) return raw;
  if (INTEGRATION_LABELS[raw]) return INTEGRATION_LABELS[raw];
  // Fallback for unknown vendors: capitalize first char, lowercase the rest.
  return raw.charAt(0) + raw.slice(1).toLowerCase();
}

export default class LocationOverviewHrisAuditLogController extends Controller {
  @service declare flashMessages: FlashMessagesService;
  @service declare state: StateService;

  queryParams = [
    'page',
    'pageSize',
    'query',
    'sortBy',
    'sortDirection',
    'eventType',
    'stepType',
    'status',
    'locationIds',
    'includeGlobal',
    'startDate',
    'endDate',
    'integration',
    'exportId',
  ];

  @tracked page = 1;
  @tracked pageSize = 25;
  @tracked query: string | undefined;
  @tracked private _inputQuery: string | undefined;
  private _searchTimer: ReturnType<typeof setTimeout> | null = null;

  get inputQuery(): string {
    return this._inputQuery ?? this.query ?? '';
  }
  @tracked sortBy: string | undefined;
  @tracked sortDirection: string | undefined;
  @tracked eventType: string | undefined;
  @tracked stepType: string | undefined;
  @tracked status: string | undefined;
  @tracked locationIds: string | undefined;
  @tracked includeGlobal: string | undefined;
  @tracked startDate: string | undefined;
  @tracked endDate: string | undefined;

  @tracked selectedEntry: DisplayAuditEntry | null = null;
  @tracked selectedEntryIndex: number | null = null;

  @tracked selectedActivities: { value: string; label: string }[] = [];

  @tracked selectedLocationIds: { value: string; label: string }[] = [];

  @tracked selectedIntegrations: { value: string; label: string }[] = [];
  @tracked integration: string | undefined;

  @tracked isExporting = false;
  @tracked exportId: string | undefined;

  // Column visibility — first 4 cols are locked (always shown)
  @tracked showColumnsMenu = false;
  @tracked colIntegration = true;
  @tracked colLocation = true;
  @tracked colMode = true;
  @tracked colAttempt = true;
  @tracked colInitiatedBy = true;
  @tracked colEventId = true;

  activityOptions = ACTIVITY_OPTIONS;
  statusOptions = STATUS_OPTIONS;
  dateRangeFilterOptions = DATE_RANGE_PRESETS;

  get entries(): DisplayAuditEntry[] {
    const raw = (this.model as { items?: AuditEntry[] })?.items ?? [];
    const tz = this.timezone;
    return raw.map((r) => enrichEntry(r, tz));
  }

  get total(): number {
    return (this.model as { total?: number })?.total ?? 0;
  }

  get unauthorized(): boolean {
    return (this.model as { unauthorized?: boolean })?.unauthorized ?? false;
  }

  get pageCount(): number {
    return (this.model as { total_pages?: number })?.total_pages ?? Math.ceil(this.total / this.pageSize);
  }

  get visibleColumnCount(): number {
    return (
      4 + // locked: timestamp, status, employee, activity
      (this.hasFailures ? 1 : 0) + // dynamic: reason column when the current page has failures
      (this.colIntegration ? 1 : 0) +
      (this.colLocation ? 1 : 0) +
      (this.colMode ? 1 : 0) +
      (this.colAttempt ? 1 : 0) +
      (this.colInitiatedBy ? 1 : 0) +
      (this.colEventId ? 1 : 0)
    );
  }

  // Drives the dynamic "Reason" column — only rendered when at least one row
  // on the current page has a failure_reason populated. Mirrors the pattern
  // in the Health Dashboard's Reason column next to Outcome=Failed.
  get hasFailures(): boolean {
    return this.entries.some((e) => !!e.failure_reason);
  }

  get timezone(): string {
    return this.state.currentLocation?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  get start(): Date {
    return parseStartDate(this.startDate ?? null, todayAsString(this.timezone), this.timezone);
  }

  get end(): Date {
    return parseEndDate(this.endDate ?? null, todayAsString(this.timezone), this.timezone);
  }

  get allTimeMin(): Date {
    return modifyDateInTimeZone(this.state.currentLocation.createdAt, this.timezone, (d) => d);
  }

  get isFiltered(): boolean {
    return !!(
      this.eventType ||
      this.stepType ||
      this.status ||
      this.locationIds ||
      this.startDate ||
      this.endDate ||
      this.query ||
      this.selectedActivities.length > 0 ||
      this.selectedLocationIds.length > 0 ||
      this.selectedIntegrations.length > 0
    );
  }

  get correlationSiblings(): DisplayAuditEntry[] {
    const correlationId = this.selectedEntry?.correlation_id;
    if (!correlationId) return [];
    return this.entries.filter((e) => e.correlation_id === correlationId).sort((a, b) => a.id - b.id);
  }

  get correlationSiblingIndex(): number {
    if (!this.selectedEntry) return -1;
    return this.correlationSiblings.findIndex((e) => e.id === this.selectedEntry!.id);
  }

  get hasPrev(): boolean {
    return this.correlationSiblingIndex > 0;
  }

  get hasNext(): boolean {
    const idx = this.correlationSiblingIndex;
    return idx >= 0 && idx < this.correlationSiblings.length - 1;
  }

  get cascadeStepsUpToCurrent(): Array<{ id: number; step_type?: string; result?: string; activity: string }> {
    if (!this.selectedEntry) return [];
    const currentId = this.selectedEntry.id;
    const siblings = this.correlationSiblings;
    const idx = siblings.findIndex((s) => s.id === currentId);
    return (idx >= 0 ? siblings.slice(0, idx + 1) : siblings).map((e) => ({
      id: e.id,
      step_type: e.step_type,
      result: e.result,
      activity: e.activity,
    }));
  }

  get selectedEntryForDrawer(): DisplayAuditEntry | null {
    return this.selectedEntry;
  }

  get locationOptions(): { value: string; label: string }[] {
    const locations = (this.model as { locations?: Array<{ id: string | number; name?: string }> })?.locations ?? [];
    return [
      { value: GLOBAL_LOCATION_VALUE, label: 'Global' },
      ...locations.map((l) => ({ value: String(l.id), label: l.name ?? String(l.id) })),
    ];
  }

  get integrationOptions(): { value: string; label: string }[] {
    const observed = (this.model as { integrations?: string[] })?.integrations ?? [];
    const union = Array.from(new Set([...observed, ...KNOWN_INTEGRATIONS])).sort();
    return union.map((name) => ({ value: name, label: name }));
  }

  get currentSelectedStatuses(): { value: string; label: string }[] {
    if (!this.status) return [];
    return this.status.split(',').flatMap((v) => {
      const opt = STATUS_OPTIONS.find((o) => o.value === v.trim());
      return opt ? [opt] : [];
    });
  }

  @action setPage(page: number): void {
    this.page = page;
  }

  @action setQuery(value: string): void {
    this._inputQuery = value;
    if (this._searchTimer !== null) clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(() => {
      this.query = value || undefined;
      this.page = 1;
    }, 300);
  }

  @action setSortBy(col: string): void {
    if (this.sortBy === col) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = col;
      this.sortDirection = 'asc';
    }
    this.page = 1;
  }

  @action setEventType(value: string | undefined): void {
    this.eventType = value === 'All events' ? undefined : value;
    this.page = 1;
  }

  @action setStatus(value: string | undefined): void {
    this.status = !value ? undefined : value;
    this.page = 1;
  }

  @action setLocationIds(value: string | undefined): void {
    this.locationIds = value;
    this.page = 1;
  }

  @action setSelectedStatuses(opts: { value: string; label: string }[]): void {
    this.status = opts.length ? opts.map((o) => o.value).join(',') : undefined;
    this.page = 1;
  }

  @action selectDateRange(startDate: Date, endDate: Date): void {
    this.startDate = formatForDateTimeQueryParam(startDate, this.timezone);
    this.endDate = formatForDateTimeQueryParam(endDate, this.timezone);
    this.page = 1;
  }

  @action toggleColumnsMenu(): void {
    this.showColumnsMenu = !this.showColumnsMenu;
  }

  @action closeColumnsMenu(): void {
    this.showColumnsMenu = false;
  }

  @action toggleColumn(col: string): void {
    (this as unknown as ColumnToggleKeys)[col as keyof ColumnToggleKeys] = !(this as unknown as ColumnToggleKeys)[
      col as keyof ColumnToggleKeys
    ];
  }

  @action selectEntry(entry: DisplayAuditEntry, idx: number): void {
    this.selectedEntry = entry;
    this.selectedEntryIndex = idx;
    const hasScrollBar = window.innerWidth > document.body.clientWidth;
    if (hasScrollBar) {
      document.body.style.marginRight = `${window.innerWidth - document.body.clientWidth}px`;
    }
    document.body.classList.add('overflow-hidden');
  }

  @action closePanel(): void {
    this.selectedEntry = null;
    this.selectedEntryIndex = null;
    document.body.classList.remove('overflow-hidden');
    document.body.style.marginRight = '';
  }

  @action navigatePrev(): void {
    const siblings = this.correlationSiblings;
    const idx = this.correlationSiblingIndex;
    if (idx <= 0) return;
    const prevEntry = siblings[idx - 1]!;
    this.selectedEntry = prevEntry;
    this.selectedEntryIndex = this.entries.findIndex((e) => e.id === prevEntry.id);
  }

  @action navigateNext(): void {
    const siblings = this.correlationSiblings;
    const idx = this.correlationSiblingIndex;
    if (idx < 0 || idx >= siblings.length - 1) return;
    const nextEntry = siblings[idx + 1]!;
    this.selectedEntry = nextEntry;
    this.selectedEntryIndex = this.entries.findIndex((e) => e.id === nextEntry.id);
  }

  @action selectCascadeStep(stepId: number): void {
    const entries = this.entries;
    const idx = entries.findIndex((e) => e.id === stepId);
    if (idx >= 0) {
      this.selectedEntry = entries[idx] ?? null;
      this.selectedEntryIndex = idx;
    }
  }

  notifyIfUnauthorized(unauthorized: boolean): void {
    if (unauthorized) {
      this.flashMessages.showAndHideFlash('error', 'You are not authorized to view the audit log.');
    }
  }

  @action copyField(value: string): void {
    try {
      void navigator.clipboard?.writeText(value);
      this.flashMessages.showAndHideFlash('success', 'Copied to clipboard');
    } catch (_) {
      this.flashMessages.showAndHideFlash('error', 'Failed to copy');
    }
  }

  @action setSelectedActivities(opts: { value: string; label: string }[]): void {
    this.selectedActivities = opts;
    // Step-type options are prefixed with "step_type:" and route to the step_type
    // query param; all other options are audit_event_kind values for event_type.
    const kindOpts = opts.filter((o) => !o.value.startsWith(STEP_TYPE_OPTION_PREFIX));
    const stepOpts = opts.filter((o) => o.value.startsWith(STEP_TYPE_OPTION_PREFIX));
    this.eventType = kindOpts.length ? kindOpts.map((o) => o.value).join(',') : undefined;
    this.stepType = stepOpts.length
      ? stepOpts.map((o) => o.value.slice(STEP_TYPE_OPTION_PREFIX.length)).join(',')
      : undefined;
    this.page = 1;
  }

  @action setSelectedLocationIds(value: { value: string; label: string }[]): void {
    this.selectedLocationIds = value;
    // Split the "Global" sentinel out of the numeric location ids that go into
    // the location_ids query param, and surface it as include_global.
    const numeric = value.filter((o) => o.value !== GLOBAL_LOCATION_VALUE);
    this.locationIds = numeric.length ? numeric.map((o) => o.value).join(',') : undefined;
    this.includeGlobal = value.some((o) => o.value === GLOBAL_LOCATION_VALUE) ? 'true' : undefined;
    this.page = 1;
  }

  @action setSelectedIntegrations(value: { value: string; label: string }[]): void {
    this.selectedIntegrations = value;
    this.integration = value.length ? value.map((o) => o.value).join(',') : undefined;
    this.page = 1;
  }

  @action copyEventId(value: string, e?: Event): void {
    e?.stopPropagation();
    try {
      void navigator.clipboard?.writeText(value);
      this.flashMessages.showAndHideFlash('success', 'Copied to clipboard');
    } catch (_) {
      this.flashMessages.showAndHideFlash('error', 'Failed to copy');
    }
  }

  @action downloadById(id: string): void {
    if (this.isExporting) return;
    // Clear from URL immediately so refreshing doesn't re-trigger the download.
    this.exportId = undefined;
    const host = config.hrisLifecycleApiHost || '';
    const url = `${host}/a/hris-lifecycle/api/v1/audit-events/exports/${encodeURIComponent(id)}`;
    this.isExporting = true;
    void fetch(url, { credentials: 'include' })
      .then(async (res) => {
        if (res.status === 410) {
          this.flashMessages.showAndHideFlash('error', 'This download link has expired or has already been used.');
          return;
        }
        if (!res.ok) {
          this.flashMessages.showAndHideFlash('error', 'Export not found.');
          return;
        }
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = 'audit-events.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);
      })
      .catch(() => {
        this.flashMessages.showAndHideFlash('error', 'Download failed — please try again.');
      })
      .finally(() => {
        this.isExporting = false;
      });
  }

  @action exportCsv(): void {
    if (this.isExporting) return;

    const parts: string[] = [];
    if (this.query) parts.push(`query=${encodeURIComponent(this.query)}`);
    if (this.eventType) parts.push(`event_type=${encodeURIComponent(this.eventType)}`);
    if (this.stepType) parts.push(`step_type=${encodeURIComponent(this.stepType)}`);
    if (this.status) parts.push(`status=${encodeURIComponent(this.status)}`);
    if (this.locationIds) parts.push(`location_ids=${encodeURIComponent(this.locationIds)}`);
    if (this.includeGlobal) parts.push(`include_global=${encodeURIComponent(this.includeGlobal)}`);
    if (this.startDate) parts.push(`start_date=${encodeURIComponent(this.startDate)}`);
    if (this.endDate) parts.push(`end_date=${encodeURIComponent(this.endDate)}`);
    if (this.integration) parts.push(`integration=${encodeURIComponent(this.integration)}`);

    const host = config.hrisLifecycleApiHost || '';
    const url = `${host}/a/hris-lifecycle/api/v1/audit-events/export?${parts.join('&')}`;
    this.isExporting = true;
    void fetch(url, { credentials: 'include' })
      .then(async (res) => {
        if (res.status === 202) {
          this.flashMessages.showAndHideFlash(
            'success',
            'Export too large to download directly — check your email for a download link.',
          );
          return;
        }
        if (!res.ok) {
          this.flashMessages.showAndHideFlash('error', `Export failed (${res.status})`);
          return;
        }
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = 'audit-events.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);
      })
      .catch(() => {
        this.flashMessages.showAndHideFlash('error', 'Export failed — please try again.');
      })
      .finally(() => {
        this.isExporting = false;
      });
  }

  @action clearFilters(): void {
    this.eventType = undefined;
    this.stepType = undefined;
    this.status = undefined;
    this.locationIds = undefined;
    this.includeGlobal = undefined;
    this.startDate = undefined;
    this.endDate = undefined;
    this.query = undefined;
    this._inputQuery = '';
    this.selectedActivities = [];
    this.selectedLocationIds = [];
    this.selectedIntegrations = [];
    this.integration = undefined;
    this.page = 1;
  }
}
